// Radha — the six batch module panels: Cashflow, CRM, Invoicing,
// Reconciliation, Forecasts, Reminders. Panels only; DirectShell owns the
// header, tabs and sign-out. Each reads live tenant-scoped rows via PostgREST.
import React, { useMemo } from "react";

import {
  useCashflowPeriods, useInvoices, useBankTxns,
  useRevenueForecasts, useReminderSchedules,
} from "../hooks/useBatchModules";
import { ModuleProposals } from "../components/ModuleProposals";
import { ModuleChart } from "../components/ModuleChart";
import { useTableSearch, SearchBar, ExportButton } from "../components/TableSearch";
import { useMe, useRole } from "../lib/authSupabase";
import { upiPayLink } from "../lib/upi";
import { PayButton } from "../components/PayButton";
import { BankConnectivity } from "../components/BankConnectivity";
import { InvoiceDiscounting } from "../components/InvoiceDiscounting";
import { usePushZohoPayment, zohoErrorMessage } from "../hooks/useZohoSync";
import { usePaymentPredictions, useInvoiceAnomalies, anomalyText } from "../hooks/usePredictions";
import { useCustomerPaymentProfiles, useInvoiceApStatuses, effectivePaymentChannel } from "../hooks/useApProcess";
import { ApStatusCell, ChannelSelect, EmailInvoiceCell } from "../components/ApStatusCell";
import { useInvoiceEmailAttempts } from "../hooks/useInvoiceEmail";
import { fmtMoney as inr, toBase as toBaseAmount, fmtDateShort } from "../lib/money";
import { FxPanel } from "../components/FxPanel";
import { SettlementOutlook } from "../components/GlobalDeskPanels";
import { CashForecastPanel } from "../components/CashForecastPanel";
import { EclPanel } from "../components/EclPanel";

const month = (d: string) => (d ? d.slice(0, 7) : "—");

const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 500 };
const td: React.CSSProperties = { padding: "10px 8px" };
// right-aligned, non-wrapping — for currency/numeric columns, matching the
// convention already used on Overview's own Outstanding column.
const numTd: React.CSSProperties = { ...td, textAlign: "right", whiteSpace: "nowrap" };
// "irn_pending" -> "Irn pending" — raw snake_case enum values read as a data
// leak, not a label, to anyone outside engineering.
const statusLabel = (s: string) => s.replace(/_/g, " ");
const statusTd: React.CSSProperties = { ...td, textTransform: "capitalize" };

function PushToZohoButton({ invoiceId, amount }: { invoiceId: string; amount: number }) {
  const push = usePushZohoPayment();
  const [note, setNote] = React.useState<string | null>(null);
  const go = () => {
    setNote(null);
    push.mutate(invoiceId, {
      onSuccess: () => setNote("Pushed"),
      onError: (e) => setNote(zohoErrorMessage(e)),
    });
  };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <button onClick={go} disabled={push.isPending || amount <= 0} title="Push this payment to the linked Zoho Books invoice"
        style={{ fontSize: 11, color: "var(--fg-muted)", background: "none", border: "1px solid var(--border)", borderRadius: 6, padding: "2px 6px", cursor: amount > 0 ? "pointer" : "default", opacity: amount > 0 ? 1 : 0.5 }}>
        {push.isPending ? "…" : "→ Zoho"}
      </button>
      {note && <span style={{ fontSize: 10.5, color: note === "Pushed" ? "var(--status-good)" : "var(--status-critical)" }}>{note}</span>}
    </span>
  );
}

function InvoiceAnomalies() {
  const { data: anomalies, isLoading } = useInvoiceAnomalies();
  if (isLoading || !anomalies || anomalies.length === 0) return null;
  return (
    <div className="sanctuary-card" style={{ padding: "12px 16px", marginBottom: 16, borderColor: "var(--status-warning)" }}>
      <div style={{ font: "600 13px Inter", marginBottom: 6 }}>Flagged for review ({anomalies.length})</div>
      <div style={{ display: "grid", gap: 4 }}>
        {anomalies.map((a, i) => (
          <div key={a.invoice_id + i} style={{ fontSize: 12.5, color: a.severity === "critical" ? "var(--status-critical)" : "var(--status-warning)" }}>
            <strong>{a.invoice_number}</strong> ({a.customer_name}) — {anomalyText(a)}
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>{label}</div>
      <div style={{ font: "600 20px Inter", color: "var(--fg)" }}>{value}</div>
    </div>
  );
}

type Col = string | { label: string; align: "right" };

function Frame({
  loading, error, stats, cols, children, proposalsTable, chart, toolbar, exportName, exportRows,
}: {
  loading: boolean; error: unknown; stats: React.ReactNode;
  cols: Col[]; children: React.ReactNode; proposalsTable?: string;
  chart?: React.ReactNode; toolbar?: React.ReactNode;
  exportName?: string; exportRows?: () => Record<string, unknown>[];
}) {
  if (loading) return <p style={{ padding: 24, color: "var(--fg-muted)" }}>Loading…</p>;
  if (error) return <p style={{ padding: 24, color: "var(--status-critical)" }}>{(error as Error).message}</p>;
  return (
    <div className="sanctuary-reveal">
      <div style={{ display: "flex", gap: 24, margin: "4px 0 20px", flexWrap: "wrap" }}>{stats}</div>
      {chart}
      {proposalsTable && <ModuleProposals table={proposalsTable} />}
      {(toolbar || exportName) && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "0 0 12px" }}>
          {toolbar}
          {exportName && exportRows && <ExportButton name={exportName} rows={exportRows} />}
        </div>
      )}
      <div className="sanctuary-card" style={{ padding: "4px 14px", overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 12, borderBottom: "1px solid var(--border)" }}>
              {cols.map((c) => {
                const label = typeof c === "string" ? c : c.label;
                const align = typeof c === "string" ? undefined : c.align;
                return <th key={label} style={{ ...th, textAlign: align ?? "left" }}>{label}</th>;
              })}
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
}

// Row-shaped wrapper over lib/money's toBase — see the reasoning there.
const toBase = (r: { total_amount: string; tax_amount?: string; fx_rate_at_issue: string | null }, field: "total_amount" | "tax_amount" = "total_amount") =>
  toBaseAmount(r[field], r.fx_rate_at_issue);

const sum = <T,>(rows: T[] | undefined, f: (r: T) => number) =>
  (rows ?? []).reduce((s, r) => s + f(r), 0);

const groupSum = <T,>(rows: T[] | undefined, key: (r: T) => string, val: (r: T) => number) => {
  const m = new Map<string, number>();
  (rows ?? []).forEach((r) => m.set(key(r), (m.get(key(r)) ?? 0) + val(r)));
  return [...m.entries()].map(([label, value]) => ({ label, value }));
};
const groupCount = <T,>(rows: T[] | undefined, key: (r: T) => string) => {
  const m = new Map<string, number>();
  (rows ?? []).forEach((r) => m.set(key(r), (m.get(key(r)) ?? 0) + 1));
  return [...m.entries()].map(([label, value]) => ({ label, value }));
};

// ---- Cashflow --------------------------------------------------------------
export function CashflowPanel() {
  const { data, isLoading, error } = useCashflowPeriods();
  const { canWrite } = useRole();
  const net = sum(data, (r) => Number(r.net_cashflow));
  const { q, setQ, filtered } = useTableSearch(data, (r) => `${r.period_month} ${r.status}`);
  return (
    <>
    <CashForecastPanel />
    <SettlementOutlook />
    <FxPanel canWrite={canWrite} />
    <Frame loading={isLoading} error={error} proposalsTable="cashflow_proposals"
      chart={<ModuleChart title="Net cashflow by month" money
        data={(data ?? []).map((r) => ({ label: month(r.period_month), value: Number(r.net_cashflow) }))} />}
      toolbar={<SearchBar q={q} setQ={setQ} count={filtered.length} total={(data ?? []).length} placeholder="Search month / status…" />}
      exportName="Cashflow" exportRows={() => filtered.map((r) => ({
        Month: month(r.period_month), Inflows: Number(r.inflows), Outflows: Number(r.outflows),
        Net: Number(r.net_cashflow), Closing: Number(r.closing_balance), Status: r.status,
      }))}
      stats={<>
        <Stat label="Periods" value={String((data ?? []).length)} />
        <Stat label="Net (sum)" value={inr(net)} />
        <Stat label="Inflows" value={inr(sum(data, (r) => Number(r.inflows)))} />
        <Stat label="Outflows" value={inr(sum(data, (r) => Number(r.outflows)))} />
      </>}
      cols={["Month", { label: "Inflows", align: "right" }, { label: "Outflows", align: "right" }, { label: "Net", align: "right" }, { label: "Closing", align: "right" }, "Status"]}>
      {filtered.map((r) => (
        <tr key={r.id} style={{ borderTop: "1px solid var(--border)" }}>
          <td style={td}>{month(r.period_month)}</td>
          <td style={numTd}>{inr(r.inflows)}</td>
          <td style={numTd}>{inr(r.outflows)}</td>
          <td style={{ ...numTd, color: Number(r.net_cashflow) < 0 ? "var(--status-critical)" : "var(--status-good)" }}>{inr(r.net_cashflow)}</td>
          <td style={numTd}>{inr(r.closing_balance)}</td>
          <td style={statusTd}>{statusLabel(r.status)}</td>
        </tr>
      ))}
    </Frame>
    </>
  );
}

// CRM moved to its own enhanced panel — see pages/CrmLive.tsx.

// ---- Invoicing -------------------------------------------------------------
export function InvoicingPanel() {
  const { data, isLoading, error } = useInvoices();
  const me = useMe(true);
  const { canWrite } = useRole();
  const { data: predictions } = usePaymentPredictions();
  const { data: paymentProfiles } = useCustomerPaymentProfiles();
  const { data: apStatuses } = useInvoiceApStatuses();
  const { data: emailAttempts } = useInvoiceEmailAttempts();
  const profileByCustomer = useMemo(
    () => new Map((paymentProfiles ?? []).map((p) => [p.customer_name, p])), [paymentProfiles],
  );
  const apStatusByInvoice = useMemo(
    () => new Map((apStatuses ?? []).map((s) => [s.invoice_id, s])), [apStatuses],
  );
  const emailAttemptsByInvoice = useMemo(() => {
    const m = new Map<string, typeof emailAttempts>();
    for (const a of emailAttempts ?? []) m.set(a.invoice_id, [...(m.get(a.invoice_id) ?? []), a]);
    return m;
  }, [emailAttempts]);
  // total invoiced revenue per customer — the size signal the auto-default
  // (058/059) uses; current outstanding swings with payment timing, this doesn't.
  const totalInvoicedByCustomer = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of data ?? []) m.set(r.customer_name, (m.get(r.customer_name) ?? 0) + Number(r.total_amount));
    return m;
  }, [data]);
  // Infinity defaults while `me` is still loading -> everyone reads as direct_pay
  // (the pre-feature behaviour) instead of flashing Email/PO cells before the
  // tenant's real thresholds arrive.
  const directPayMax = me?.direct_pay_max_threshold ?? Infinity;
  const poPortalMin = me?.po_portal_auto_threshold ?? Infinity;
  const noIrn = (data ?? []).filter((r) => r.irn_required && !r.irn).length;
  const { q, setQ, filtered } = useTableSearch(data, (r) => `${r.invoice_number} ${r.customer_name} ${r.status}`);
  return (
    <>
    <InvoiceAnomalies />
    <InvoiceDiscounting invoices={data ?? []} canWrite={canWrite} />
    <Frame loading={isLoading} error={error} proposalsTable="invoice_proposals"
      chart={<ModuleChart title="Invoiced by status" money color="var(--accent-purple)"
        data={groupSum(data, (r) => r.status, (r) => toBase(r))} />}
      toolbar={<SearchBar q={q} setQ={setQ} count={filtered.length} total={(data ?? []).length} placeholder="Search invoice / customer…" />}
      exportName="Invoices" exportRows={() => filtered.map((r) => ({
        Invoice: r.invoice_number, Customer: r.customer_name, Taxable: Number(r.taxable_amount),
        Tax: Number(r.tax_amount), Total: Number(r.total_amount),
        IRN: r.irn ? "yes" : r.irn_required ? "required" : "n/a", Status: r.status,
      }))}
      stats={<>
        <Stat label="Invoices" value={String((data ?? []).length)} />
        <Stat label="Total" value={inr(sum(data, (r) => toBase(r)))} />
        <Stat label="Tax" value={inr(sum(data, (r) => toBase(r, "tax_amount")))} />
        <Stat label="IRN pending" value={String(noIrn)} />
      </>}
      cols={["Invoice", "Customer", { label: "Taxable", align: "right" }, { label: "Tax", align: "right" }, { label: "Total", align: "right" }, "IRN", "Status", "Predicted pay", "Pay"]}>
      {filtered.map((r) => {
        const pred = predictions?.get(r.id);
        return (
        <tr key={r.id} style={{ borderTop: "1px solid var(--border)" }}>
          <td style={td}>{r.invoice_number}</td>
          <td style={td}>{r.customer_name}</td>
          <td style={numTd}>{inr(r.taxable_amount, r.currency)}</td>
          <td style={numTd}>{inr(r.tax_amount, r.currency)}</td>
          <td style={numTd}>{inr(r.total_amount, r.currency)}</td>
          <td style={{ ...td, color: r.irn ? "var(--status-good)" : r.irn_required ? "var(--status-critical)" : "var(--fg-muted)" }}>
            {r.irn ? "✓" : r.irn_required ? "required" : "n/a"}
          </td>
          <td style={statusTd}>{statusLabel(r.status)}</td>
          <td style={{ ...td, fontSize: 12 }} title={pred?.basis}>
            {pred ? (
              <span style={{ color: Number(pred.confidence) >= 0.6 ? "var(--fg)" : "var(--fg-muted)" }}>
                {fmtDateShort(pred.predicted_pay_date)}
                <span style={{ color: "var(--fg-faint)" }}> ({Math.round(Number(pred.confidence) * 100)}%)</span>
              </span>
            ) : <span style={{ color: "var(--fg-muted)" }}>—</span>}
          </td>
          <td style={td}>
            {(() => {
              const outstanding = Number(r.total_amount) - Number(r.amount_paid);
              const amountPaid = Number(r.amount_paid);
              const zohoLinked = !!(r.zoho_invoice_id && r.zoho_customer_id);
              const profile = profileByCustomer.get(r.customer_name);
              const channel = effectivePaymentChannel(profile, totalInvoicedByCustomer.get(r.customer_name) ?? 0, directPayMax, poPortalMin);
              const selector = <ChannelSelect customerName={r.customer_name} effectiveChannel={channel} isExplicit={!!profile} canWrite={canWrite} />;
              if (channel === "po_portal") {
                if (outstanding <= 0) return <span style={{ color: "var(--fg-muted)" }}>—</span>;
                return (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <ApStatusCell invoiceId={r.id} apStatus={apStatusByInvoice.get(r.id)} canWrite={canWrite} />
                    {selector}
                  </span>
                );
              }
              if (channel === "invoice_email") {
                if (outstanding <= 0) return <span style={{ color: "var(--fg-muted)" }}>—</span>;
                return (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <EmailInvoiceCell invoiceId={r.id} attempts={emailAttemptsByInvoice.get(r.id) ?? []} canWrite={canWrite} />
                    {selector}
                  </span>
                );
              }
              return (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  {outstanding > 0 ? (
                    <PayButton invoiceId={r.id} outstanding={outstanding} tenantName={me?.tenant_legal_name ?? "Radha"} label="Pay" />
                  ) : <span style={{ color: "var(--fg-muted)" }}>—</span>}
                  {outstanding > 0 && me?.tenant_upi_vpa && (
                    <a href={upiPayLink(me.tenant_upi_vpa, me.tenant_legal_name ?? "Radha", outstanding, r.invoice_number)}
                      title="Pay via any UPI app (no gateway confirmation, mark paid manually)"
                      style={{ fontSize: 11, color: "var(--fg-muted)", textDecoration: "none", border: "1px solid var(--border)", borderRadius: 6, padding: "2px 6px" }}>
                      UPI
                    </a>
                  )}
                  {zohoLinked && amountPaid > 0 && <PushToZohoButton invoiceId={r.id} amount={amountPaid} />}
                  {outstanding > 0 && selector}
                </span>
              );
            })()}
          </td>
        </tr>
        );
      })}
    </Frame>
    </>
  );
}

// ---- Reconciliation --------------------------------------------------------
export function ReconciliationPanel() {
  const { data, isLoading, error } = useBankTxns();
  const { canWrite } = useRole();
  const unmatched = (data ?? []).filter((r) => r.status === "unmatched").length;
  const { q, setQ, filtered } = useTableSearch(data, (r) => `${r.payer_name ?? ""} ${r.bank ?? ""} ${r.utr ?? ""} ${r.vpa ?? ""}`);
  return (
    <>
    <BankConnectivity canWrite={canWrite} />
    <Frame loading={isLoading} error={error} proposalsTable="recon_proposals"
      chart={<ModuleChart title="Receipts by status" money color="var(--status-good)"
        data={groupSum(data, (r) => r.status, (r) => Number(r.amount))} />}
      toolbar={<SearchBar q={q} setQ={setQ} count={filtered.length} total={(data ?? []).length} placeholder="Search payer / bank / UTR…" />}
      exportName="Reconciliation" exportRows={() => filtered.map((r) => ({
        Payer: r.payer_name ?? "", Bank: r.bank ?? "", Amount: Number(r.amount),
        "UTR / VPA": r.utr ?? r.vpa ?? "", "Value date": r.value_date, Status: r.status, Source: r.source,
      }))}
      stats={<>
        <Stat label="Transactions" value={String((data ?? []).length)} />
        <Stat label="Received" value={inr(sum(data, (r) => Number(r.amount)))} />
        <Stat label="Unmatched" value={String(unmatched)} />
      </>}
      cols={["Payer", "Bank", { label: "Amount", align: "right" }, "UTR / VPA", "Value date", "Status", "Source"]}>
      {filtered.map((r) => (
        <tr key={r.id} style={{ borderTop: "1px solid var(--border)" }}>
          <td style={td}>{r.payer_name ?? "—"}</td>
          <td style={td}>{r.bank ?? "—"}</td>
          <td style={numTd}>{inr(r.amount)}</td>
          <td style={{ ...td, color: "var(--fg-muted)" }}>{r.utr ?? r.vpa ?? "—"}</td>
          <td style={td}>{r.value_date}</td>
          <td style={{ ...statusTd, color: r.status === "unmatched" ? "var(--status-warning)" : "var(--fg)" }}>{statusLabel(r.status)}</td>
          <td style={{ ...td, fontSize: 11, color: r.source === "account_aggregator" ? "var(--accent-purple)" : "var(--fg-faint)" }}>
            {r.source === "account_aggregator" ? "AA feed" : "manual"}
          </td>
        </tr>
      ))}
    </Frame>
    </>
  );
}

// ---- Forecasts -------------------------------------------------------------
export function ForecastsPanel() {
  const { data, isLoading, error } = useRevenueForecasts();
  const { q, setQ, filtered } = useTableSearch(data, (r) => `${r.period_month} ${r.status}`);
  return (
    <>
    <EclPanel />
    <Frame loading={isLoading} error={error} proposalsTable="forecast_proposals"
      chart={<ModuleChart title="Base forecast by month" money color="var(--accent-purple)"
        data={(data ?? []).map((r) => ({ label: month(r.period_month), value: Number(r.base_amount) }))} />}
      toolbar={<SearchBar q={q} setQ={setQ} count={filtered.length} total={(data ?? []).length} placeholder="Search month / status…" />}
      exportName="Forecasts" exportRows={() => filtered.map((r) => ({
        Month: month(r.period_month), Bear: Number(r.bear_amount), Base: Number(r.base_amount),
        Bull: Number(r.bull_amount), Actual: r.actual_amount ? Number(r.actual_amount) : "",
        Variance: r.variance_to_base == null ? "" : Number(r.variance_to_base), Status: r.status,
      }))}
      stats={<>
        <Stat label="Months" value={String((data ?? []).length)} />
        <Stat label="Base (sum)" value={inr(sum(data, (r) => Number(r.base_amount)))} />
        <Stat label="Bull" value={inr(sum(data, (r) => Number(r.bull_amount)))} />
        <Stat label="Bear" value={inr(sum(data, (r) => Number(r.bear_amount)))} />
      </>}
      cols={["Month", { label: "Bear", align: "right" }, { label: "Base", align: "right" }, { label: "Bull", align: "right" }, { label: "Actual", align: "right" }, { label: "Variance", align: "right" }, "Status"]}>
      {filtered.map((r) => (
        <tr key={r.id} style={{ borderTop: "1px solid var(--border)" }}>
          <td style={td}>{month(r.period_month)}</td>
          <td style={numTd}>{inr(r.bear_amount)}</td>
          <td style={numTd}>{inr(r.base_amount)}</td>
          <td style={numTd}>{inr(r.bull_amount)}</td>
          <td style={numTd}>{r.actual_amount ? inr(r.actual_amount) : "—"}</td>
          <td style={{ ...numTd, color: r.variance_to_base == null ? "var(--fg-muted)" : Number(r.variance_to_base) < 0 ? "var(--status-critical)" : "var(--status-good)" }}>
            {r.variance_to_base == null ? "—" : inr(r.variance_to_base)}
          </td>
          <td style={statusTd}>{statusLabel(r.status)}</td>
        </tr>
      ))}
    </Frame>
    </>
  );
}

// ---- Reminders -------------------------------------------------------------
export function RemindersPanel() {
  const { data, isLoading, error } = useReminderSchedules();
  const active = (data ?? []).filter((r) => r.is_active).length;
  const { q, setQ, filtered } = useTableSearch(data, (r) => `${r.customer_name} ${r.channel} ${r.language}`);
  return (
    <Frame loading={isLoading} error={error} proposalsTable="reminder_proposals"
      chart={<ModuleChart title="Schedules by channel" color="var(--status-warning)"
        data={groupCount(data, (r) => r.channel)} />}
      toolbar={<SearchBar q={q} setQ={setQ} count={filtered.length} total={(data ?? []).length} placeholder="Search customer / channel…" />}
      exportName="Reminders" exportRows={() => filtered.map((r) => ({
        Customer: r.customer_name, Channel: r.channel, Language: r.language,
        "Cadence (days)": r.cadence_days, "Next run": r.next_run_date ?? "", Active: r.is_active ? "yes" : "no",
      }))}
      stats={<>
        <Stat label="Schedules" value={String((data ?? []).length)} />
        <Stat label="Active" value={String(active)} />
      </>}
      cols={["Customer", "Channel", "Language", "Cadence", "Next run", "Active"]}>
      {filtered.map((r) => (
        <tr key={r.id} style={{ borderTop: "1px solid var(--border)" }}>
          <td style={td}>{r.customer_name}</td>
          <td style={td}>{r.channel}</td>
          <td style={td}>{r.language}</td>
          <td style={td}>{r.cadence_days}d</td>
          <td style={td}>{r.next_run_date ?? "—"}</td>
          <td style={{ ...td, color: r.is_active ? "var(--status-good)" : "var(--fg-muted)" }}>{r.is_active ? "yes" : "no"}</td>
        </tr>
      ))}
    </Frame>
  );
}
